import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import SendIcon from "@mui/icons-material/Send";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Grid,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import { SafeArgonEditor } from "0.Mine/4.Argon/ArgonComponents";

interface Attachment {
  file: File;
  type: "file" | "image";
}

interface EmailData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  content: string;
  attachments: Attachment[];
}

interface ComposeEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (emailData: EmailData) => void;
  displayToandCC: boolean;
}

function ComposeEmailModal({ open, onClose, onSend, displayToandCC }: ComposeEmailModalProps) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const handleSend = () => {
    onSend({ to, cc, bcc, subject, content: emailContent, attachments });
    handleClose();
  };

  const handleClose = () => {
    setTo("");
    setCc("");
    setBcc("");
    setSubject("");
    setEmailContent("");
    setAttachments([]);
    onClose();
  };

  const handleAttachment = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "file" | "image" = "file"
  ) => {
    const files = Array.from(event.target.files || []);
    const newAttachments = files.map((file) => ({ file, type }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: "80vh", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Componi Email</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {displayToandCC && (
          <>
            <TextField
              fullWidth
              label="To"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              margin="dense"
              variant="outlined"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="CC"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="BCC"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  margin="dense"
                />
              </Grid>
            </Grid>
          </>
        )}

        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          margin="dense"
        />

        <Box sx={{ flexGrow: 1, mt: 2, mb: 2, minHeight: "250px" }}>
          <SafeArgonEditor value={(content) => setEmailContent(content)} />
        </Box>

        {attachments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Attachments</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {attachments.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                    p: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {item.type === "image" ? (
                    <ImageIcon fontSize="small" sx={{ ml: 0.5 }} />
                  ) : (
                    <DescriptionIcon fontSize="small" sx={{ ml: 0.5 }} />
                  )}
                  <Typography variant="body2" sx={{ mx: 1 }}>
                    {item.file.name}
                  </Typography>
                  <IconButton size="small" onClick={() => removeAttachment(index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button component="label" startIcon={<AttachFileIcon />} variant="outlined">
            Files
            <input type="file" multiple hidden onChange={handleAttachment} />
          </Button>
          <Button component="label" startIcon={<ImageIcon />} variant="outlined">
            Images
            <input
              type="file"
              multiple
              hidden
              accept="image/*"
              onChange={(e) => handleAttachment(e, "image")}
            />
          </Button>
        </Box>
        <Box>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            disabled={displayToandCC ? !to || !subject : !subject}
          >
            Send
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ComposeEmailModal;
